import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Sparkles, BookTemplate, Upload, History, Wand2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-6 rounded-sm border-[#E5E7EB] shadow-none hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
          <div className="font-display text-4xl mt-2 text-[#0F172A]">{value ?? "—"}</div>
        </div>
        <div className={`w-10 h-10 ${accent} flex items-center justify-center rounded-sm`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
      </div>
    </Card>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentGenerated, setRecentGenerated] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/generator").then(({ data }) => setRecentGenerated((data || []).slice(0, 6))).catch(() => {});
  }, []);

  const t = stats?.totals || {};

  return (
    <PageContainer>
      <PageHeader
        title={`Hola, ${user?.name?.split(" ")[0] || ""}`}
        subtitle="Redacción jurídica asistida por IA. Combina plantillas, escritos y datos de LexDuo."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Documentos generados" value={t.generated} icon={Sparkles} accent="bg-[#7B1826]/10 text-[#7B1826]" />
        <Stat label="Plantillas" value={t.templates} icon={BookTemplate} accent="bg-indigo-50 text-indigo-700" />
        <Stat label="Biblioteca PDF/DOCX" value={t.documents} icon={FileText} accent="bg-[#1E293B]/10 text-[#1E293B]" />
        <Stat label="Subidos a LexDuo" value={t.uploaded_to_lexduo ?? "—"} icon={Upload} accent="bg-emerald-50 text-emerald-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick actions */}
        <Card className="p-6 rounded-sm border-[#E5E7EB] shadow-none">
          <h2 className="font-display text-2xl mb-4">Accesos rápidos</h2>
          <div className="space-y-2">
            <Link
              to="/generador"
              data-testid="quick-generator"
              className="flex items-center gap-3 p-3 border border-[#E5E7EB] hover:border-[#7B1826] hover:bg-[#FBFBF9] transition-colors rounded-sm"
            >
              <div className="w-8 h-8 bg-[#7B1826] text-white flex items-center justify-center rounded-sm">
                <Wand2 className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#0F172A]">Nuevo documento</div>
                <div className="text-[11px] text-neutral-500">Generador IA con expediente LexDuo</div>
              </div>
            </Link>
            <Link
              to="/documentos-generados"
              data-testid="quick-history"
              className="flex items-center gap-3 p-3 border border-[#E5E7EB] hover:border-[#7B1826] hover:bg-[#FBFBF9] transition-colors rounded-sm"
            >
              <div className="w-8 h-8 bg-[#0F172A] text-white flex items-center justify-center rounded-sm">
                <History className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#0F172A]">Ver registro completo</div>
                <div className="text-[11px] text-neutral-500">Documentos generados + trazabilidad LexDuo</div>
              </div>
            </Link>
            <Link
              to="/plantillas"
              data-testid="quick-templates"
              className="flex items-center gap-3 p-3 border border-[#E5E7EB] hover:border-[#7B1826] hover:bg-[#FBFBF9] transition-colors rounded-sm"
            >
              <div className="w-8 h-8 bg-indigo-700 text-white flex items-center justify-center rounded-sm">
                <BookTemplate className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#0F172A]">Plantillas</div>
                <div className="text-[11px] text-neutral-500">Crear y gestionar</div>
              </div>
            </Link>
          </div>
        </Card>

        {/* Recent generated docs */}
        <Card className="lg:col-span-2 p-6 rounded-sm border-[#E5E7EB] shadow-none">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl">Documentos recientes</h2>
            <Link to="/documentos-generados" className="text-xs uppercase tracking-wider text-[#7B1826] hover:underline">
              Ver todos
            </Link>
          </div>
          {recentGenerated.length === 0 ? (
            <div className="py-10 text-center text-sm text-neutral-500">
              Aún no hay documentos generados. Empieza en el <Link to="/generador" className="text-[#7B1826] hover:underline">Generador IA</Link>.
            </div>
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {recentGenerated.map((g) => (
                <Link
                  key={g.id}
                  to={`/generador?gen=${g.id}`}
                  data-testid={`dashboard-gen-${g.id}`}
                  className="flex items-center justify-between py-3 hover:bg-[#FBFBF9] px-2 -mx-2 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0F172A] truncate">{g.title}</div>
                    <div className="text-[11px] text-neutral-500 flex items-center gap-2 mt-0.5">
                      <span>{fmtDate(g.created_at)}</span>
                      {g.expediente_id_lexduo && (
                        <Badge variant="outline" className="rounded-sm text-[10px] border-[#7B1826]/30 text-[#7B1826]">
                          LexDuo #{g.expediente_id_lexduo}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {g.uploaded_to_lexduo ? (
                    <Badge className="rounded-sm text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                      Adjuntado
                    </Badge>
                  ) : g.expediente_id_lexduo ? (
                    <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 text-amber-700">
                      Pendiente
                    </Badge>
                  ) : null}
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </PageContainer>
  );
}
